// src/components/dashboard/YourClients.tsx
//
// PKT-0306 · "Your Clients" ranked next-action widget · v1
//
// Replaces "Tasks Due Today" on the dashboard. Answers "how can I be productive
// RIGHT NOW": one row per active client, ranked by who needs the operator most,
// each with ONE recommended action. Mirror of the New Leads widget (name ·
// context line · one affordance). Ranking is the pure, unit-tested scorer in
// ./yourClientsScore — this component only fetches signals and renders.
//
// R1 / read-only: reads clients + client_tasks under the operator's Supabase
// session (RLS-scoped) via the frontend-direct pattern.
// Never sends anything. The per-client cadence checklist + task CRUD stay on the
// client detail view — this widget deliberately shows NO task list (overdue
// cadence collapses into one score signal, never a wall of rows).
//
// Signal availability (v1 — see EVENT-0865): contact-staleness, stage, budget
// and collapsed cadence are live; move-in is coarse (text timeline bucket);
// unanswered-inbound and shortlist-engagement degrade to 0 until that data is
// wired client-side (the scorer already supports them).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { supabase } from "../../lib/supabase";
import { localDateStr } from "../../lib/taskCadenceDates";
import {
  selectRankedClients,
  type ClientSignals,
  type RankedClientRow,
} from "./yourClientsScore";
import { useCoordinatedRefresh } from "../../hooks/useCoordinatedRefresh";

interface YourClientsProps {
  currentUserId?: string | null;
  onOpenClient?: (clientId: string) => void;
  // Bump to force a refetch (e.g. after a convert/log seeds new data).
  refreshKey?: number;
}

const MAX_ROWS = 8;

const formatBudget = (budget: number | null): string => {
  if (typeof budget !== "number" || !Number.isFinite(budget) || budget <= 0) {
    return "";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(budget);
};

const YourClients: React.FC<YourClientsProps> = ({
  currentUserId,
  onOpenClient,
  refreshKey,
}) => {
  const theme = useTheme();
  const [rows, setRows] = useState<RankedClientRow[]>([]);
  const [rowsOwnerId, setRowsOwnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const todayStr = useMemo(() => localDateStr(), []);
  const currentUserIdRef = React.useRef(currentUserId);
  currentUserIdRef.current = currentUserId;
  const visibleRows = rowsOwnerId === currentUserId ? rows : [];

  const load = useCallback(async () => {
    const ownerUserId = currentUserId;
    const isCurrentOwner = () => currentUserIdRef.current === ownerUserId;
    if (!ownerUserId) {
      setRows([]);
      setRowsOwnerId(null);
      return;
    }
    setLoading(true);
    try {
      // 1. Clients assigned to this operator (RLS also scopes this).
      const { data: clientData, error: clientErr } = await supabase
        .from("clients")
        .select(
          "id, first_name, last_name, status, priority, max_budget, move_in_date, move_in_timeline, last_contacted_at, is_test_client",
        )
        .eq("assigned_agent_id", ownerUserId)
        .limit(500);
      if (clientErr) throw clientErr;
      const clients = (clientData as unknown as ClientSignals[]) || [];

      // Zero clients (an agent's day-one state) must short-circuit BEFORE the
      // signal queries: `.in("client_id", [])` compiles to PostgREST `in.()`,
      // which errors — the empty dashboard only worked because the catch block
      // happened to blank the rows. Empty is an answer, not an error.
      if (clients.length === 0) {
        if (isCurrentOwner()) {
          setRows([]);
          setRowsOwnerId(ownerUserId);
        }
        return;
      }

      // 2. Collapsed cadence: count pending/overdue tasks per client (ONE signal,
      //    never rendered as individual rows).
      const pendingByClient = new Map<string, number>();
      const { data: taskData, error: taskErr } = await supabase
        .from("client_tasks")
        .select("client_id")
        .eq("status", "pending")
        .lte("due_date", todayStr)
        .limit(2000);
      if (taskErr) throw taskErr;
      for (const t of (taskData as unknown as { client_id: string }[]) || []) {
        pendingByClient.set(t.client_id, (pendingByClient.get(t.client_id) ?? 0) + 1);
      }

      const signals: ClientSignals[] = clients.map((c) => ({
        ...c,
        pending_task_count: pendingByClient.get(c.id) ?? 0,
        last_inbound_at: null,
        last_outbound_at: null,
        last_shortlist_engaged_at: null,
      }));

      const clientIds = clients.map((client) => client.id);
      const [shortlistsResult, activitiesResult] = await Promise.all([
        supabase.from("client_shortlists").select("id, client_id").in("client_id", clientIds),
        supabase.from("activities").select("entity_id, created_at").eq("entity_type", "client")
          .in("entity_id", clientIds).in("activity_type", ["sent_text", "email_sent", "listing_shared"]),
      ]);
      if (shortlistsResult.error) throw shortlistsResult.error;
      if (activitiesResult.error) throw activitiesResult.error;
      const shortlistClientById = new Map((shortlistsResult.data || []).map((row: any) => [row.id, row.client_id]));
      const shortlistIds = Array.from(shortlistClientById.keys());
      const engagementByClient = new Map<string, string>();
      if (shortlistIds.length) {
        const { data: events, error } = await supabase.from("client_shortlist_events")
          .select("shortlist_id, created_at").in("shortlist_id", shortlistIds)
          .in("event_type", ["page_view", "property_expand", "interested", "tour_request"]);
        if (error) throw error;
        for (const event of events || []) {
          const clientId = shortlistClientById.get((event as any).shortlist_id);
          if (clientId && (!engagementByClient.get(clientId) || engagementByClient.get(clientId)! < (event as any).created_at)) engagementByClient.set(clientId, (event as any).created_at);
        }
      }
      const outboundByClient = new Map<string, string>();
      for (const activity of activitiesResult.data || []) {
        const row = activity as any;
        if (!outboundByClient.get(row.entity_id) || outboundByClient.get(row.entity_id)! < row.created_at) outboundByClient.set(row.entity_id, row.created_at);
      }
      for (const signal of signals) {
        signal.last_shortlist_engaged_at = engagementByClient.get(signal.id) ?? null;
        signal.last_outbound_at = outboundByClient.get(signal.id) ?? null;
      }

      if (isCurrentOwner()) {
        setRows(selectRankedClients(signals, new Date().toISOString()).slice(0, MAX_ROWS));
        setRowsOwnerId(ownerUserId);
      }
    } catch (err) {
      console.error("YourClients load failed:", err);
      // Resume failures retain last-good rows for the same identity. A changed
      // identity is render-gated by rowsOwnerId and can never see prior rows.
    } finally {
      if (isCurrentOwner()) setLoading(false);
    }
  }, [currentUserId, todayStr]);

  const requestRefresh = useCoordinatedRefresh(load, { requestOnMount: false });
  useEffect(() => {
    requestRefresh();
  }, [currentUserId, refreshKey, requestRefresh]);

  return (
    <Card sx={{ mb: 3, minHeight: 180 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <GroupIcon sx={{ color: "primary.main" }} />
            <Typography variant="h6" fontWeight={600}>
              Your Clients
            </Typography>
          </Box>
          {visibleRows.length > 0 && (
            <Chip label={visibleRows.length} size="small" color="primary" sx={{ fontWeight: 700 }} />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Ranked by who needs you right now · one action each
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={22} />
          </Box>
        ) : visibleRows.length === 0 ? (
          <Box py={2} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              All caught up — no assigned clients need you right now. Nice work. ✨
            </Typography>
          </Box>
        ) : (
          <Box>
            {visibleRows.map((row) => (
              <Box
                key={row.id}
                onClick={() => onOpenClient?.(row.id)}
                sx={{
                  minHeight: 48,
                  px: 0.5,
                  py: 1,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  transition: "background-color 160ms ease",
                  "&:last-of-type": { borderBottom: 0 },
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  <ArrowForwardIcon fontSize="small" />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {row.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {row.stage} · {row.whyNow}
                  </Typography>
                </Box>
                <Box sx={{ flexShrink: 0, textAlign: "right", ml: 1 }}>
                  {formatBudget(row.budget) && (
                    <Typography variant="body2" color="primary.main" fontWeight={700} noWrap>
                      {formatBudget(row.budget)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {row.action}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default YourClients;
